/**
 * Labels, tones and the two or three judgement calls the console makes about
 * how machine output should look.
 *
 * ## Severity has colour. Confidence does not.
 *
 * Severity is a property of the WORLD — a critical incident is critical whoever
 * reported it — so it gets the app's status colours like any other severity.
 * Confidence is a property of the MACHINE's own claim, and painting the model's
 * self-assessment in green would give it exactly the authority this whole module
 * is built to withhold. Confidence is rendered as a word and a rationale.
 *
 * ## A contested finding is marked, not hidden and not reddened
 *
 * When the red team refused to accept a finding the analyst kept, the console
 * says so beside it. It is not an error state — it is a disagreement between two
 * models from two companies, which is more informative than either one alone,
 * and dressing it as a failure would teach people to skip those.
 */

export const SEVERITY_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'] as const

export const SEVERITY_TONE: Record<string, 'bad' | 'warn' | 'good' | 'muted'> = {
  CRITICAL: 'bad',
  HIGH: 'bad',
  MEDIUM: 'warn',
  LOW: 'muted',
  INFO: 'muted',
}

export const SEVERITY_CLASS: Record<string, string> = {
  CRITICAL: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400',
  HIGH: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400',
  MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400',
  LOW: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-400',
  INFO: 'bg-muted text-muted-foreground border-border',
}

export const FINDING_KIND_LABEL: Record<string, string> = {
  RISK: 'Risk',
  OPPORTUNITY: 'Opportunity',
  ANOMALY: 'Anomaly',
  TREND: 'Trend',
  SECURITY: 'Security',
  COMPLIANCE: 'Compliance',
  DATA_QUALITY: 'Data quality',
  PERSUADABILITY: 'Persuadability',
}

export const RUN_STATUS_LABEL: Record<string, string> = {
  QUEUED: 'Queued',
  BUILDING_BRIEF: 'Building brief',
  RUNNING: 'Consulting the council',
  COMPLETED: 'Complete',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
  BLOCKED_BUDGET: 'Stopped by budget',
}

/** A run is still moving — the console keeps polling while this is true. */
export const isLiveRun = (status: string) =>
  status === 'QUEUED' || status === 'BUILDING_BRIEF' || status === 'RUNNING'

export const STEP_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Waiting',
  RUNNING: 'Thinking',
  OK: 'Answered',
  FAILED: 'Failed',
  SKIPPED_NO_MODEL: 'No model matched',
  SKIPPED_NO_DIVERSE_VENDOR: 'No second vendor',
  SKIPPED_BUDGET: 'Over budget',
}

export const STAGE_KIND_LABEL: Record<string, string> = {
  SCOUT: 'Scout',
  ANALYST: 'Analyst',
  RED_TEAM: 'Red team',
  EDITOR: 'Editor',
  CREATIVE: 'Creative',
  ROUTER: 'Router',
}

export const STAGE_BLURB: Record<string, string> = {
  SCOUT: 'Reads one slice of the brief. Cheap and parallel.',
  ANALYST: 'Synthesises the findings. The most capable model available.',
  RED_TEAM: 'Tries to refute the analyst — and must be a different vendor.',
  EDITOR: 'Writes the report against a fixed schema and picks the charts.',
  CREATIVE: 'Turns an accepted recommendation into an executable brief.',
  ROUTER: 'Works out what the question is about.',
}

export const PROPOSAL_KIND_LABEL: Record<string, string> = {
  NOTE: 'Note',
  VOTER_TAG: 'Tag voters',
  PROGRAM_BUDGET_SHIFT: 'Move programme budget',
  MOBILIZER_ACTION: 'Mobiliser action',
  RELOCATION_PLAN: 'Relocation plan',
  SURVEY_LAUNCH: 'Field a poll',
  INCIDENT_ESCALATION: 'Escalate an incident',
  RBAC_REVIEW: 'Review access',
  CREDENTIAL_ROTATION: 'Rotate a credential',
  CONTENT_BRIEF: 'Content brief',
  WATCHLIST_ADD: 'Add to a watchlist',
  NOTIFICATION_BROADCAST: 'Broadcast',
}

export const PROPOSAL_STATUS_LABEL: Record<string, string> = {
  PROPOSED: 'Awaiting a decision',
  APPROVED: 'Approved, not yet applied',
  REJECTED: 'Rejected',
  APPLIED: 'Applied',
  FAILED: 'Application failed',
  EXPIRED: 'Expired',
}

export const CATEGORY_LABEL: Record<string, string> = {
  CAMPAIGN: 'Campaign',
  OPPOSITION: 'Opposition',
  OPERATIONS: 'Operations',
  FINANCE: 'Finance',
  SECURITY: 'Security',
  FIELD: 'Field',
  CREATIVE: 'Creative',
  ANALYST: 'Analyst',
}

export const CATEGORY_ORDER = [
  'FIELD', 'CAMPAIGN', 'OPERATIONS', 'FINANCE', 'OPPOSITION', 'SECURITY', 'CREATIVE',
]

export const CATALOGUE_STATUS_LABEL: Record<string, string> = {
  UNVERIFIED: 'Not checked',
  VERIFIED: 'Confirmed by the provider',
  MISSING_AT_PROVIDER: 'The provider does not list it',
}

/** A dataset id printed as the model is told to cite it. */
export const datasetRef = (id?: string | null) => (id ? `dataset: ${id}` : 'no dataset')

export const usd = (n?: number | null, digits = 4) =>
  n == null ? '—' : `$${Number(n).toFixed(n < 1 ? digits : 2)}`

export const kes = (usdAmount?: number | null, rate = 129) =>
  usdAmount == null ? '—' : `KES ${Math.round(usdAmount * rate).toLocaleString()}`

/** Percent, currency or plain — whichever the editor asked the chart to use. */
export function formatValue(value: number | null, format?: string): string {
  if (value == null || Number.isNaN(value)) return '—'
  if (format === 'percent') return `${value.toFixed(1)}%`
  if (format === 'currency') return value.toLocaleString(undefined, { maximumFractionDigits: 0 })
  const abs = Math.abs(value)
  if (abs >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 })
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
}
